import React, { useState, useRef, useEffect } from 'react'

import SgSelection from './overlays/SgSelection'

import { getStroke } from 'perfect-freehand'
// import { getStrokePoints } from 'perfect-freehand'
import { throttle } from 'lodash'

import { preventDefault, stopEventPropagation } from '../utils/dom'
import {
  getCanvasCoord,
  addCoordinates,
  subtractCoordinates,
  getTranslateDelta,
  getStrokeRegeneration,
  SCOPE_MIN,
  SCOPE_MAX,
  STANDARD_VIEWPORT_WITH,
  STANDARD_VIEWPORT_HEIGHT,
  adjustTranslateCoordinate,
} from '../utils/utils'

import { handleGestureDown } from '../events/pointerDown/handleGestureDown'
import { handleGestureMove } from '../events/pointerMove/handleGestureMove'
import { handleSelectDown } from '../events/pointerDown/handleSelectDown'
import { handleSelectMove } from '../events/pointerMove/handleSelectMove'
import { handleSelectUp } from '../events/pointerUp/handleSelectUp'
import { handlePenDown } from '../events/pointerDown/handlePenDown'
import { handlePenMove } from '../events/pointerMove/handlePenMove'
import { handlePenUp } from '../events/pointerUp/handlePenUp'
import { handleShapeDown } from '../events/pointerDown/handleShapeDown'
import { handleEraseMove } from '../events/pointerMove/handleEraseMove'
import { handleEraseUp } from '../events/pointerUp/handleEraseUp'
import { handleShapeMove } from '../events/pointerMove/handleShapeMove'
import { handleShapeUp } from '../events/pointerUp/handleShapeUp'
import { handleCancelToolAction } from '../events/pointerUp/handleCancelToolAction'
import { handleBlockEraserDown } from '../events/pointerDown/handleBlockEraserDown'
import { handleBlockEraserMove } from '../events/pointerMove/handleBlockEraserMove'
import { handleBlockEraserUp } from '../events/pointerUp/handleBlockEraserUp'

export default function Canvas({
  boardId,
  isWritable,
  isiOSAfterImageStyleActive,
  ///////////
  paths,
  setPaths,
  yPathsRef,
  triggerAddPath,
  triggerUpdatePath,
  triggerUpdateTranslateAndScope,
  ydocRef,
  yScopeRef,
  yTranslateRef,
  // [useState]
  isPenMode,
  setIsPenMode,
  isStylePanelVisible,
  setIsStylePanelVisible,
  isMoreButtonVisible,
  setIsMoreButtonVisible,
  isShapeFilled,
  isSimulatePressure,
  setIsSimulatePressure,
  scope,
  setScope,
  translate,
  setTranslate,
  selectedTool,
  setSelectedTool,
  selectedShape,
  colorVal,
  thicknessVal,
  opacityVal,
  selectionRect,
  setSelectionRect,
  selectionRectPathKeys,
  setSelectionRectPathKeys,
  lockedCheckKey,
  setLockedCheckKey,
  historyStack,
  setHistoryStack,
  imageUrls,
  // [useRef]
  scaleRatio,
  getBoundingClientSgCanvas,
}) {
  /**
   * State
   * [ 상태 관리 변수 ]
   */
  const [isDragging, setIsDragging] = useState(false)
  const [isSelectedMovingActive, setIsSelectedMovingActive] = useState(false)
  const [isResizeActive, setIsResizeActive] = useState(false)

  const [isCapTrue, setIsCapTrue] = useState(true)

  // tool: 2 (move)
  const [isMoving, setIsMoving] = useState(false)

  // tool: 4 (eraser)
  const [isErasingActive, setIsErasingActive] = useState(false)

  const [isBlockEraserActive, setIsBlockEraserActive] = useState(false)
  const [blockEraserPosition, setBlockEraserPosition] = useState({ top: 0, left: 0 })
  const blockEraserScaledHalfSizeRef = useRef({ width: 1, height: 1 })

  /**
   * Ref
   * [ 참조 변수 ]
   */

  const pointerDownTimeRef = useRef(0)
  // state의 비동기 업데이트로 인해 useRef를 통해 최신값 저장
  const scopeRef = useRef(1)
  const translateRef = useRef(translate)
  // gesture
  const moveActivePointsRef = useRef([])
  const downDistanceRef = useRef(null)
  const downCenterRef = useRef(null)
  const downScopeRef = useRef(null)
  const downTranslateRef = useRef(null)

  // previous point coordinate
  const previousPenPoint = useRef({ x: 0, y: 0 })
  const previousErasePoint = useRef({ x: 0, y: 0 })
  const previousMovePoint = useRef({ x: 0, y: 0 })

  // tool: 1(select)
  const pathsAtPointerDown = useRef({})
  const draggingRect = useRef({ top: 0, left: 0, right: 0, bottom: 0, width: 0, height: 0 })
  const draggingStartPos = useRef({ x: 0, y: 0 })
  const draggingCurrentPos = useRef({ x: 0, y: 0 })
  const selectCoordinateAtPointerDown = useRef({ x: 0, y: 0 })
  const tmpSelectionRect = useRef({})
  const tmpSelectionRectPathKeys = useRef([])
  const resizeDirection = useRef('')
  // tool: 2(move)
  const translateAtPointerDown = useRef({ x: 0, y: 0 })
  // tool: 3(path))
  const currentPath = useRef(null)
  const currentPathId = useRef(0)
  // tool: 4(eraser)
  const erasedPathKeys = useRef([])

  /**
   * ***********************************************************************************************
   * < resize effect >
   * ***********************************************************************************************
   */
  useEffect(() => {
    const adjustCanvas = () => {
      const sgCanvasElement = document.getElementById('sg-canvas')
      getBoundingClientSgCanvas.current = {
        x: sgCanvasElement.getBoundingClientRect().left,
        y: sgCanvasElement.getBoundingClientRect().top,
      }
      const widthRatio = sgCanvasElement.getBoundingClientRect().width / STANDARD_VIEWPORT_WITH
      const heightRatio = sgCanvasElement.getBoundingClientRect().height / STANDARD_VIEWPORT_HEIGHT

      // 화면_사이즈 / (1 * 상대_비율) = 비율에 맞는 기준 크기
      // adjustCanvas : 캔버스를 정확한 비율로 중앙에 위치시키기 위해 실제 화면 사이즈를 기준으로 "얼마만큼의 여백이 필요하냐"를 계산
      // scaleRatio : 현재 디바이스에서 1 스코프일 때, 실제로 몇 배 확대된 상태인가?

      // [ case: 가로가 더 넓은 경우 ]
      if (widthRatio >= heightRatio) {
        // 현재 높이 기준으로 계산했을 때, 기준 비율에 맞춰 확장한 "기준 가로 크기"
        let totalWidth = sgCanvasElement.getBoundingClientRect().width / (1 * heightRatio)
        // let widthByHeightRatio =
        //   (sgCanvasElement.getBoundingClientRect().height / (1 * heightRatio)) *
        //   (STANDARD_VIEWPORT_WITH / STANDARD_VIEWPORT_HEIGHT)
        let widthByHeightRatio = STANDARD_VIEWPORT_WITH
        setTranslate({ x: (totalWidth - widthByHeightRatio) / 2, y: 0 })

        // scope
        setScope(1 * heightRatio)
        scaleRatio.current = heightRatio
        scopeRef.current = 1 * scaleRatio.current
      }
      // [ case: 세로가 더 긴 경우 ]
      else {
        // 현재 너비 기준으로 계산했을 때, 기준 비율에 맞춰 확장한 "기준 세로 크기"
        let totalHeight = sgCanvasElement.getBoundingClientRect().height / (1 * widthRatio)
        // let heightByWidthRatio =
        //   (sgCanvasElement.getBoundingClientRect().width / (1 * widthRatio)) *
        //   (STANDARD_VIEWPORT_HEIGHT / STANDARD_VIEWPORT_WITH)
        let heightByWidthRatio = STANDARD_VIEWPORT_HEIGHT
        setTranslate({ x: 0, y: (totalHeight - heightByWidthRatio) / 2 })

        // scope
        setScope(1 * widthRatio)
        scaleRatio.current = widthRatio
        scopeRef.current = 1 * scaleRatio.current
      }
    }

    // [ 초기 실행 ]
    adjustCanvas()

    // [ 브라우저 전체 창 리사이즈 대응 ]
    const onResize = () => adjustCanvas()
    window.addEventListener('resize', onResize)

    // [ 전자칠판 초기 로딩 문제 해결 ]
    const sgCanvasElement = document.getElementById('sg-canvas')
    const observer = sgCanvasElement ? new ResizeObserver(() => adjustCanvas()) : null
    if (observer && sgCanvasElement) observer.observe(sgCanvasElement)

    return () => {
      window.removeEventListener('resize', onResize)
      if (observer) observer.disconnect()
    }
    // eslint-disable-next-line
  }, [boardId])

  /**
   * ***********************************************************************************************
   * < keyboard 이벤트 처리 effect >
   * ***********************************************************************************************
   */
  useEffect(() => {
    const onKeyDown = (e) => {
      /* [ Change Mode ] */
      // <Select>
      if (e.key === 'v') setSelectedTool('select')
      // <Move>
      if (e.key === 'm') setSelectedTool('move')
      // <Pen>
      if (e.key === 'd') setSelectedTool('pen')
      // <Eraser>
      if (e.key === 'e') setSelectedTool('eraser')
      // <Shape>
      if (e.key === 's') setSelectedTool('shape')

      /* [ Select all path ] */
      if ((e.metaKey && e.key === 'a') || (e.ctrlKey && e.key === 'A')) {
        if (Object.keys(paths).length) {
          let selectedPathKeys = []
          for (let key of Object.keys(paths)) {
            if (paths[key].isLocked === true) continue
            selectedPathKeys.push(key)
          }
          if (lockedCheckKey) setLockedCheckKey('')
          setSelectionRectPathKeys(selectedPathKeys)
          setSelectedTool('select')
        }
      }

      /* [ Erase all path ] */
      if (e.key === 'Backspace') {
        if (selectionRectPathKeys.length) {
          // 선택된 객체 삭제
          for (let pathKey of Object.keys(paths)) {
            if (selectionRectPathKeys.includes(pathKey)) {
              delete paths[pathKey]
              // [동시편집 - 삭제]
              yPathsRef.current.delete(pathKey)
            }
          }
          if (lockedCheckKey) setLockedCheckKey('')
          setPaths({ ...paths })
          setSelectionRectPathKeys([])
          setSelectionRect({ top: 0, left: 0, width: 0, height: 0 })
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
    // eslint-disable-next-line
  }, [paths, selectionRectPathKeys])

  /**
   * ***********************************************************************************************
   * < wheel 이벤트 처리 effect >
   * ***********************************************************************************************
   */
  useEffect(() => {
    const sgCanvasElement = document.getElementById('sg-canvas')

    const onWheel = (e) => {
      preventDefault(e)

      /* [ Scope 마우스휠 조정 ] */
      // cmd + mouseWheel
      if (e.metaKey || e.ctrlKey) {
        const clientCoord = subtractCoordinates(
          { x: e.clientX, y: e.clientY },
          getBoundingClientSgCanvas.current
        )

        if (e.deltaY) {
          let nextScope = Math.round(scopeRef.current * 100 - Math.round(e.deltaY / 40)) / 100
          nextScope =
            nextScope <= SCOPE_MIN * scaleRatio.current
              ? SCOPE_MIN * scaleRatio.current
              : nextScope >= SCOPE_MAX * scaleRatio.current
              ? SCOPE_MAX * scaleRatio.current
              : nextScope
          const translateDelta = getTranslateDelta(
            clientCoord.x,
            clientCoord.y,
            nextScope,
            scopeRef.current
          )

          scopeRef.current = nextScope
          setScope(nextScope)
          // yScopeRef.current?.set('scope', nextScope / scaleRatio.current)

          setTranslate((prev) => {
            const updateTranslate = adjustTranslateCoordinate(
              addCoordinates(prev, translateDelta),
              nextScope,
              scaleRatio.current
            )

            translateRef.current = updateTranslate
            return updateTranslate
          })

          requestAnimationFrame(() => {
            triggerUpdateTranslateAndScope(translateRef.current, nextScope)
          })
        }
        return
      }

      /* [ Translate 상하좌우 ] */
      if (e.shiftKey) {
        setTranslate((prev) => {
          const updateTranslate = adjustTranslateCoordinate(
            { x: prev.x - e.deltaX, y: prev.y },
            scope,
            scaleRatio.current
          )

          translateRef.current = updateTranslate
          return updateTranslate
        })
        requestAnimationFrame(() => {
          triggerUpdateTranslateAndScope(translateRef.current, scope)
        })
      } else {
        setTranslate((prev) => {
          const updateTranslate = adjustTranslateCoordinate(
            { x: prev.x, y: prev.y - e.deltaY },
            scope,
            scaleRatio.current
          )

          translateRef.current = updateTranslate
          return updateTranslate
        })
        requestAnimationFrame(() => {
          triggerUpdateTranslateAndScope(translateRef.current, scope)
        })
      }
    }

    sgCanvasElement.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      sgCanvasElement.removeEventListener('wheel', onWheel)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    scopeRef.current = scope
  }, [scope])

  // ************************************************************
  // * onPointerDown - 포인터 다운 이벤트 핸들러
  // ************************************************************

  /**
   * 포인터 다운 이벤트 발생 시 실행되는 함수.
   *
   * 이 함수는 다음과 같은 역할을 수행한다:
   * 1. **제스처 감지 및 초기화** (멀티 터치 이동, 확대/축소)
   * 2. **각 도구별 동작 처리** (선택, 펜, 지우개, 도형 등)
   * 3. **UI 패널(스타일 패널, 추가 버튼) 상태 관리**
   *
   * e.pointerId: 포인터에 고유하게 할당된 식별자
   * e.buttons: 버튼 상태 [0: no button / 1: left / 2: right / 4: center(wheel)]
   * e.isPrimary: 이벤트가 기본 포인터로부터 발생했는지 여부(마우스,펜: true / 손: 첫번째 입력 true)
   * e.pointerType: mouse / touch / pen
   *
   */
  const onPointerdown = (e) => {
    preventDefault(e)
    if (!isWritable) return

    // ************************************************************
    // * [1] 기본 포인터 이벤트 처리 (Pointer ID, 버튼 예외 처리 등)
    // ************************************************************

    e.target.setPointerCapture(e.pointerId)

    // 스타일 패널이 열려 있다면 닫음
    if (isStylePanelVisible) {
      setIsStylePanelVisible(false)
      pointerDownTimeRef.current = performance.now()
    }

    // 더보기 버튼이 표시된 상태라면 숨김
    if (isMoreButtonVisible) setIsMoreButtonVisible(false)

    // 입력 버튼 예외 처리 (왼쪽 클릭 또는 휠 클릭이 아니면 반환)
    if (e.buttons !== 1 && e.buttons !== 4) return

    // 펜 입력 모드 예외 처리 (펜이 감지되면 펜 모드 활성화)
    if (!isPenMode && e.pointerType === 'pen') setIsPenMode(true)

    // ************************************************************
    // * [2] 포인터 좌표 저장 (싱글 터치 / 멀티 터치 관리)
    // ************************************************************

    if (e.isPrimary) {
      moveActivePointsRef.current = [
        { id: e.pointerId, x: e.clientX, y: e.clientY, time: performance.now() },
      ]
    } else {
      moveActivePointsRef.current.push({
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        time: performance.now(),
      })
    }

    // 현재 포인터의 클라이언트 좌표 계산
    const clientCoord = subtractCoordinates(
      { x: e.clientX, y: e.clientY },
      getBoundingClientSgCanvas.current
    )

    //////////////////
    // [ 영역 지우개 ] //
    //////////////////
    const blockEraserState = {
      clientCoord,
      translate,
      scope,
      scaleRatio,
      blockEraserScaledHalfSizeRef,
      setIsBlockEraserActive,
      setBlockEraserPosition,
    }
    if (handleBlockEraserDown(e, blockEraserState)) return

    // ************************************************************
    // * [3] 이동 tool 기능 & 제스처 감지 및 처리 (단일 터치 이동, 멀티 터치 이동, 확대/축소)
    // ************************************************************

    const gestureState = {
      moveActivePointsRef,
      previousMovePoint,
      translateAtPointerDown,
      translate,
      clientCoord,
      getBoundingClientSgCanvas,
      downCenterRef,
      downDistanceRef,
      downScopeRef,
      downTranslateRef,
      selectedTool,
      previousErasePoint,
      // 상태
      paths,
      currentPath,
      currentPathId,
      erasedPathKeys,
      scope,
      isDragging,
      isSelectedMovingActive,
      isResizeActive,
      // setter 함수
      setPaths,
      setIsMoving,
      setIsDragging,
      setIsSelectedMovingActive,
      setIsResizeActive,
      setIsErasingActive,
      // 동시편집
      yPathsRef,
    }
    if (handleGestureDown(e, gestureState)) return

    // pen mode touch exception
    if (isPenMode && e.pointerType !== 'pen' && e.isPrimary === true) return

    // ************************************************************
    // * [4] 도구별 동작 처리
    // ************************************************************

    /**
     * [Tool] Select (선택 도구)
     */
    if (selectedTool === 'select') {
      const selectState = {
        boardId,
        // dragging
        setIsDragging,
        setSelectionRect,
        setSelectionRectPathKeys,
        draggingRect,
        draggingStartPos,
        draggingCurrentPos,
        // selectionBox move&&resize
        clientCoord,
        pathsAtPointerDown,
        selectCoordinateAtPointerDown,
        paths,
        scope,
        translate,
        selectionRect,
        tmpSelectionRect,
        selectionRectPathKeys,
        setIsResizeActive,
        setIsSelectedMovingActive,
        resizeDirection,
        // history
        historyStack,
        setHistoryStack,
      }
      handleSelectDown(e, selectState)
    }

    /**
     * [tool]
     * pen-pressure / pen / pen-highlight
     */
    if (
      selectedTool === 'pen-pressure' ||
      selectedTool === 'pen' ||
      selectedTool === 'pen-highlight'
    ) {
      const penState = {
        paths,
        currentPath,
        currentPathId,
        scope,
        translate,
        selectedTool,
        setIsCapTrue,
        setIsSimulatePressure,
        thicknessVal,
        opacityVal,
        colorVal,
        clientCoord,
        previousPenPoint,
        // 동시편집
        triggerAddPath,
      }
      handlePenDown(e, penState)
    }

    /**
     * [tool]
     * eraser
     */
    if (selectedTool === 'eraser') {
      setIsErasingActive(true)
    }

    /**
     * [tool]
     * shape
     */
    if (selectedTool === 'shape') {
      const shapeState = {
        paths,
        currentPath,
        currentPathId,
        selectedTool,
        selectedShape,
        clientCoord,
        scope,
        translate,
        isShapeFilled,
        thicknessVal,
        opacityVal,
        colorVal,
        previousPenPoint,
        // 동시편집
        triggerAddPath,
      }
      handleShapeDown(e, shapeState)
    }
  }

  // ************************************************************
  // * onPointerMove - 포인터 이동 이벤트 핸들러
  // ************************************************************

  /**
   * 포인터 이동 이벤트 발생 시 실행되는 함수.
   *
   * 이 함수는 다음과 같은 역할을 수행한다:
   * 1. ** 제스처 감지 및 처리 ** (멀티 터치 이동, 확대/축소)
   * 2. ** 각 도구별 이동 처리 ** (선택 도구, 펜, 지우개, 도형 등)
   *
   */

  const onPointermove = (e) => {
    preventDefault(e)
    if (!isWritable) return

    // ************************************************************
    // * [1] 기본 포인터 이벤트 예외 처리
    // ************************************************************

    // 입력 버튼 예외 처리 (왼쪽 클릭 또는 휠 클릭이 아니면 반환)
    if (e.buttons !== 1 && e.buttons !== 4) return

    // 현재 포인터의 클라이언트 좌표 계산
    const clientCoord = subtractCoordinates(
      { x: e.clientX, y: e.clientY },
      getBoundingClientSgCanvas.current
    )

    if (
      previousPenPoint.current.x === getCanvasCoord(clientCoord, translate, scope).x &&
      previousPenPoint.current.y === getCanvasCoord(clientCoord, translate, scope).y
    )
      return
    else {
      previousPenPoint.current = getCanvasCoord(clientCoord, translate, scope)
    }

    //////////////////
    // [ 영역 지우개 ] //
    //////////////////
    if (isBlockEraserActive) {
      const blockEraserState = {
        clientCoord,
        translate,
        scope,
        paths,
        erasedPathKeys,
        blockEraserScaledHalfSizeRef,
        setBlockEraserPosition,
      }
      handleBlockEraserMove(blockEraserState)
      return
    }

    // ************************************************************
    // * [2] 이동 tool 기능 & 제스처 감지 및 처리 (단일 터치 이동, 멀티 터치 이동, 확대/축소)
    // ************************************************************
    if (isMoving) {
      const gestureState = {
        moveActivePointsRef,
        // 단일 터치
        clientCoord,
        previousMovePoint,
        translateAtPointerDown,
        // 멀티 터치
        getBoundingClientSgCanvas,
        downScopeRef,
        downDistanceRef,
        downCenterRef,
        downTranslateRef,
        scope,
        setScope,
        scaleRatio,
        scopeRef,
        setTranslate,
        selectedTool,
        // 동시편집
        triggerUpdateTranslateAndScope,
      }

      if (handleGestureMove(e, gestureState)) return
    }

    // ************************************************************
    // * [3] 도구별 이동 처리
    // ************************************************************

    const throttleHandleToolMove = throttle((e, clientCoord) => {
      // 기본 포인터(primary pointer)만 처리 (멀티 터치의 보조 포인터는 제외)
      if (!e.isPrimary) return
      // onPointerDown에서 path 초기값 생성되지 않은 경우 이벤트 처리 X
      if (selectedTool === 'pen-pressure' && !currentPath.current) return // pen-pressure
      if (selectedTool === 'pen' && !currentPath.current) return // pen
      if (selectedTool === 'pen-highlight' && !currentPath.current) return // pen-highlight
      if (selectedTool === 'shape' && !currentPath.current) return // shape

      /**
       * [Tool] Select (선택 도구)
       */
      if (selectedTool === 'select') {
        const selectState = {
          isDragging,
          tmpSelectionRectPathKeys,
          selectionRectPathKeys,
          draggingCurrentPos,
          draggingStartPos,
          clientCoord,
          scope,
          translate,
          draggingRect,
          paths,
          setSelectionRectPathKeys,
          isSelectedMovingActive,
          selectCoordinateAtPointerDown,
          pathsAtPointerDown,
          selectionRect,
          tmpSelectionRect,
          setSelectionRect,
          isResizeActive,
          resizeDirection,
          isSimulatePressure,
          isCapTrue,
          // 동시편집
          ydocRef,
          triggerUpdatePath,
        }
        handleSelectMove(selectState)
      }

      /**
       * [tool]
       * pen-pressure / pen / pen-highlight
       */
      if (
        selectedTool === 'pen-pressure' ||
        selectedTool === 'pen' ||
        selectedTool === 'pen-highlight'
      ) {
        const penState = {
          clientCoord,
          scope,
          translate,
          paths,
          currentPath,
          currentPathId,
          selectedTool,
          isCapTrue,
          isSimulatePressure,
          thicknessVal,
          opacityVal,
          colorVal,
          previousPenPoint,
          // 동시편집
          triggerAddPath,
          triggerUpdatePath,
        }
        handlePenMove(e, penState)
      }

      /**
       * [tool]
       * eraser
       */
      if (selectedTool === 'eraser') {
        const eraseState = {
          isErasingActive,
          clientCoord,
          scope,
          translate,
          paths,
          previousErasePoint,
          erasedPathKeys,
        }
        handleEraseMove(eraseState)
      }

      /**
       * [tool]
       * shape
       */
      if (selectedTool === 'shape') {
        const shapeState = {
          paths,
          currentPath,
          currentPathId,
          scope,
          translate,
          clientCoord,
          // 동시편집
          triggerUpdatePath,
        }
        handleShapeMove(shapeState)
      }
    }, 10)

    throttleHandleToolMove(e, clientCoord)
  }

  // ************************************************************
  // * onPointerUp - 포인터 업 이벤트 핸들러
  // ************************************************************

  /**
   * 포인터 업 이벤트 발생 시 실행되는 함수.
   *
   * 이 함수는 다음과 같은 역할을 수행한다:
   * 1. **빠른 클릭 취소 감지 및 처리** (`handleCancelToolAction`)
   * 2. **제스처 이동 종료 및 초기화**
   * 3. **각 도구별 동작 완료 처리** (선택, 펜, 지우개, 도형 등)
   *
   */
  const onPointerup = (e) => {
    if (!isWritable) return

    //////////////////
    // [ 영역 지우개 ] //
    //////////////////
    if (isBlockEraserActive) {
      const blockEraserState = {
        paths,
        erasedPathKeys,
        setIsBlockEraserActive,
        boardId,
        ydocRef,
        yPathsRef,
        historyStack,
        setHistoryStack,
      }
      handleBlockEraserUp(blockEraserState)

      return
    }

    // ************************************************************
    // * [1] 빠른 클릭 취소 감지 및 처리 (클릭 시간이 150ms 이하일 경우)
    // ************************************************************
    if (pointerDownTimeRef.current && performance.now() - pointerDownTimeRef.current < 100) {
      const toolActionState = {
        selectedTool,
        isDragging,
        setIsDragging,
        isSelectedMovingActive,
        setIsSelectedMovingActive,
        isResizeActive,
        setIsResizeActive,
        setIsErasingActive,
        erasedPathKeys,
        paths,
        setPaths,
        currentPath,
        currentPathId,
        previousErasePoint,
        pointerDownTimeRef,
        // 동시편집
        yPathsRef,
      }
      if (handleCancelToolAction(toolActionState)) return
    }

    // ************************************************************
    // * [2] 기본적인 포인터 업 이벤트 처리
    // ************************************************************

    const clientCoord = subtractCoordinates(
      { x: e.clientX, y: e.clientY },
      getBoundingClientSgCanvas.current
    )

    if (isMoving) {
      setIsMoving(false)
      if (selectedTool === 'move') {
        previousMovePoint.current = { x: 0, y: 0 }
        translateAtPointerDown.current = { x: 0, y: 0 }
        return
      }
    }

    // 기본 포인터(primary pointer)만 처리 (멀티 터치의 보조 포인터는 제외)
    // onPointerDown에서 path 초기값이 생성되지 않았다면 처리 X
    if (
      (selectedTool === 'pen-pressure' ||
        selectedTool === 'pen' ||
        selectedTool === 'pen-highlight' ||
        selectedTool === 'shape') &&
      !currentPath.current
    )
      return

    // ************************************************************
    // * [3] 도구별 동작 완료 처리
    // ************************************************************

    /**
     * [Tool] Select (선택 도구)
     */
    if (selectedTool === 'select') {
      const selectState = {
        boardId,
        ///////
        paths,
        scope,
        translate,
        selectionRect,
        setSelectionRect,
        selectionRectPathKeys,
        setSelectionRectPathKeys,
        setLockedCheckKey,
        // dragging
        isDragging,
        setIsDragging,
        draggingStartPos,
        // move
        isSelectedMovingActive,
        setIsSelectedMovingActive,
        // resize
        isResizeActive,
        setIsResizeActive,
        // extra
        clientCoord,
        selectCoordinateAtPointerDown,
        historyStack,
        setHistoryStack,
        // 동시편집
        ydocRef,
        triggerUpdatePath,
        pathsAtPointerDown,
      }
      handleSelectUp(e, selectState)
    }

    /**
     * [tool]
     * pen-pressure / pen / pen-highlight
     */
    if (
      selectedTool === 'pen-pressure' ||
      selectedTool === 'pen' ||
      selectedTool === 'pen-highlight'
    ) {
      const penState = { boardId, currentPath, currentPathId, historyStack, setHistoryStack }
      handlePenUp(penState)
    }

    /**
     * [tool]
     * eraser
     */
    if (selectedTool === 'eraser') {
      const eraseState = {
        boardId,
        ////////
        paths,
        erasedPathKeys,
        setIsErasingActive,
        previousErasePoint,
        historyStack,
        setHistoryStack,
        // 동시편집
        ydocRef,
        yPathsRef,
      }
      handleEraseUp(eraseState)
    }

    /**
     * [tool]
     * shape
     */
    if (selectedTool === 'shape') {
      const shapeState = {
        boardId,
        ////////
        paths,
        currentPath,
        currentPathId,
        historyStack,
        setHistoryStack,
        // 동시편집
        triggerUpdatePath,
      }
      handleShapeUp(shapeState)
    }
  }

  /* ---------------- onClick ------------------ */

  const onClick = (e) => {
    stopEventPropagation(e)
  }

  /* -------------- onTouchStart ---------------- */

  const onTouchStart = (e) => {
    preventDefault(e)
  }

  /* -------------- onTouchMove ---------------- */

  const onTouchMove = (e) => {
    preventDefault(e)
  }

  /* -------------- onTouchEnd ---------------- */

  const onTouchEnd = (e) => {
    preventDefault(e)
  }

  /* -------------- onDragOver ---------------- */

  const onDragOver = (e) => {
    preventDefault(e)
  }

  /* ---------------- onDrop ------------------ */

  const onDrop = (e) => {
    preventDefault(e)
  }

  return (
    <div
      id='sg-canvas'
      className='sg-canvas'
      // pointer event
      onPointerDown={onPointerdown}
      onPointerMove={onPointermove}
      onPointerUp={onPointerup}
      // mouse event
      onClick={onClick}
      // tablet event
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* sg-canvas > sg-shapes */}
      <div
        id='sg-shapes'
        className='sg-html-layer sg-shapes'
        style={{
          transform: `scale(${scope}) translate(${translate.x}px, ${translate.y}px)`,
        }}
      >
        {/* sg-html-layer > background */}
        {Object.entries(paths).map(([key, value], idx) => (
          <div key={key} id={key}>
            <div
              key={key}
              id={`sg-shape-${key}`}
              className='sg-shape'
              style={{
                top: value.y,
                left: value.x,
                width: value.w,
                height: value.h,
                opacity: value.opacity,
                zIndex: key === 'background' ? 4000 : 4000 + value.zIndex,
              }}
            >
              {value.type === 'image' ? (
                ///////////////
                // [ image ] //
                ///////////////
                <div className='sg-relative'>
                  <div
                    id={`sg-image-container-${key}`}
                    className='sg-image-container'
                    style={{
                      position: 'relative',
                      transform: `scaleX(${value.scaleX}) scaleY(${value.scaleY})`,
                      transformOrigin: 'top left',
                    }}
                  >
                    {value.src in imageUrls && (
                      <img
                        id={`path-${key}`}
                        src={imageUrls[value.src]}
                        alt='error'
                        className='sg-pointer-events-auto'
                        style={{
                          width: value.w,
                          height: value.h,
                        }}
                        crossOrigin='anonymous'
                      />
                    )}
                  </div>
                </div>
              ) : (
                /////////////////////
                // [ draw && geo ] //
                /////////////////////
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  id={`sg-svg-container-${key}`}
                  className='sg-svg-container'
                  width={value.w}
                  height={value.h}
                  viewBox={`0 0 ${value.w} ${value.h}`}
                  preserveAspectRatio='none'
                >
                  <path
                    id={`path-${key}`}
                    className=''
                    stroke={value.stroke}
                    strokeWidth={value.strokeWidth}
                    fill={value.fill}
                    d={value.d}
                    fillRule='nonzero'
                    clipRule='nonzero'
                    // shapeRendering='geometricPrecision'
                  />
                </svg>
              )}
            </div>

            {/* selected path: selection 활성화 표시 */}
            {selectionRectPathKeys.includes(key) && (
              <div
                className='sg-shape-overlay'
                style={{
                  position: 'absolute',
                  top: paths[key].y,
                  left: paths[key].x,
                  width: paths[key].w,
                  height: paths[key].h,
                  zIndex: 99999,
                }}
              >
                <svg xmlns='http://www.w3.org/2000/svg' className='sg-svg-container-overlay'>
                  <path
                    stroke='#5CA6F1'
                    strokeWidth={2}
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    // d={getSvgPathFromStroke(
                    //   getStroke(paths[key].points, getStrokeOptions(1, isSimulatePressure, isCapTrue))
                    // )}
                    d={getStrokeRegeneration(
                      getStroke,
                      1,
                      isSimulatePressure,
                      isCapTrue,
                      key,
                      paths,
                      paths[key].points
                    )}
                  />
                </svg>
              </div>
            )}

            {/* 포인트 체크 로직 위치 */}
          </div>
        ))}
      </div>

      {/* sg-canvas > sg-overlays */}
      <div id='sg-overlays' className='sg-overlays'>
        <div
          className='sg-html-layer'
          style={{
            transform: `scale(${scope}) translate(${translate.x}px, ${translate.y}px)`,
          }}
        >
          {/* sg-overlays > draggingBox */}
          <div
            id='sg-selection_dragging'
            className={`${
              isDragging ? 'sg-absolute' : 'sg-hidden'
            } sg-border sg-border-gray-500 sg-border-solid sg-bg-[#EDEDED] sg-opacity-50`}
            style={{
              top: `${draggingRect.current.top}px`,
              left: `${draggingRect.current.left}px`,
              width: `${draggingRect.current.width}px`,
              height: `${draggingRect.current.height}px`,
            }}
          />

          {/* sg-overlays > selectionBox */}
          <SgSelection
            paths={paths}
            scope={scope}
            selectionRect={selectionRect}
            setSelectionRect={setSelectionRect}
            selectionRectPathKeys={selectionRectPathKeys}
            setSelectionRectPathKeys={setSelectionRectPathKeys}
            isResizeActive={isResizeActive}
          />

          {/* sg-overlays > eraser */}
          {selectedTool === 'eraser' && (
            <div
              id='eraser'
              className='sg-absolute sg-hidden sg-w-10 sg-h-10 sg-bg-gray-500 sg-rounded-full sg-opacity-50'
              style={{ top: 0, left: 0, transform: `scale(${1 / scope})` }}
            />
          )}
          {/* sg-overlays > blockEraser */}
          {isBlockEraserActive && (
            <div
              id='blockEraser'
              className='sg-absolute sg-rounded-[8px] sg-border-[3px] sg-border-sk-primary sg-origin-top-left'
              style={{
                top: blockEraserPosition.top,
                left: blockEraserPosition.left,
                width: blockEraserScaledHalfSizeRef.current.width * 2 * scope,
                height: blockEraserScaledHalfSizeRef.current.height * 2 * scope,
                transform: `scale(${1 / scope})`,
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
